import React, {
  Children,
  forwardRef,
  isValidElement,
  type PropsWithChildren,
  useRef,
  useState,
  type MouseEvent,
} from 'react';

import classNames from 'classnames';

import { ChevronDownSmallIcon } from '@cognite/cogs-icons-v11/system-icons/ChevronDownSmallIcon.tsx';
import { ChevronUpSmallIcon } from '@cognite/cogs-icons-v11/system-icons/ChevronUpSmallIcon.tsx';
import { EllipsisHorizontalIcon } from '@cognite/cogs-icons-v11/system-icons/EllipsisHorizontalIcon.tsx';
import { IconWrapper } from '@cognite/cogs-utils-v11/components/icon-wrapper/icon-wrapper.tsx';
import { getThemeClassNames } from '@cognite/cogs-utils-v11/helpers/get-theme-class-names/get-theme-class-names.ts';
import { useBoolean } from '@cognite/cogs-utils-v11/hooks/use-boolean.ts';

import { Body } from '../../tokens';
import { IconButton } from '../_technical';
import { Button } from '../button';
import { useConfigContextLoose } from '../config-provider';
import { Dropdown } from '../dropdown';
import { Flex } from '../flex';
import { Menu } from '../menu';
import { Tooltip } from '../tooltip';

import type {
  BreadcrumbsHtmlElementProps,
  BreadcrumbsItemProps,
} from './types';
import { useResponsiveBreadcrumbs } from './use-responsive-breadcrumbs';
import { useResponsiveMaxWidth } from './use-responsive-max-width';

const Separator = () => (
  <Body className="cogs-v11 cogs-breadcrumbs__separator">/</Body>
);

/**
 * Breadcrumbs
 * =============================================================================
 * Breadcrumbs is secondary navigation displaying hierarchy or navigational path.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-breadcrumb--base)
 *
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Breadcrumb>
 *   <Breadcrumbs.Item title="Home" link="#" icon={<HomeIcon />} />
 *   <Breadcrumbs.Item title="Assets" link="#" />
 *   <Breadcrumbs.Item title="Industrial" link="#" />
 *   <Breadcrumbs.Item title="Current" link="#" active />
 * </Breadcrumb>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-breadcrumb--base)
 */
type BreadcrumbsComponentType = ((
  props: BreadcrumbsHtmlElementProps<HTMLDivElement>
) => React.ReactElement) & {
  Item: React.ForwardRefExoticComponent<
    BreadcrumbsHtmlElementProps<HTMLDivElement> &
      BreadcrumbsItemProps &
      React.RefAttributes<HTMLDivElement>
  >;
};

export const Breadcrumbs: BreadcrumbsComponentType = ({
  children,
  className,
  isCollapsed: isCollapsedAllAtOnce,
  isResponsive,
  inverted,
  themeProps,
}: BreadcrumbsHtmlElementProps<HTMLDivElement>): React.ReactElement => {
  const childrenArr = Children.toArray(children);
  const items: BreadcrumbsItemProps[] = childrenArr
    .filter((child) => isValidElement<BreadcrumbsItemProps>(child))
    .map((child) => child.props);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const mainWrapperRef = useRef<HTMLElement>(null);

  const { visibleItemsLeftSide, shouldTruncateActiveItem, collapsedItems } =
    useResponsiveBreadcrumbs({
      children,
      items,
      itemRefs,
      mainWrapperRef,
      isResponsive,
      isCollapsedAllAtOnce,
    });

  // we need the keys to update if children change, so we use prefix
  // dependent on all children. this is necessary for responsive
  // behavior to work correctly in case the children change
  const breadcrumbItemRenderKeyPrefix = items
    .map((item) => item.label)
    .join('_');

  const getItemKey = (item: BreadcrumbsItemProps) =>
    `${breadcrumbItemRenderKeyPrefix}_${item.label}_${item.icon}_${item.link}`;

  const renderBreadcrumbItems = ({ start = 0, end = 0 }) => (
    <>
      {items.slice(start, end).map((child, i) => {
        const isActive = i + start + 1 === items.length;
        return (
          <Breadcrumbs.Item
            {...child}
            key={getItemKey(child)}
            active={isActive}
            aria-current={isActive ? 'page' : 'false'}
            ref={(el: HTMLDivElement) => (itemRefs.current[i + start] = el)}
            _parentWrapperRef={
              shouldTruncateActiveItem ? mainWrapperRef : undefined
            }
          />
        );
      })}
    </>
  );

  return (
    <nav
      ref={mainWrapperRef}
      aria-label="Breadcrumbs"
      className={classNames(
        'cogs-v11',
        'cogs-breadcrumbs',
        className,
        ...getThemeClassNames(inverted, themeProps)
      )}
    >
      <Flex
        element="ol"
        direction="row"
        alignItems="center"
        justifyContent="flex-start"
      >
        {renderBreadcrumbItems({ start: 0, end: visibleItemsLeftSide })}
        {collapsedItems > 0 && (
          <>
            <div className="cogs-v11 cogs-breadcrumbs__menu__wrapper">
              <Dropdown
                hideOnSelect={{
                  hideOnContentClick: true,
                  hideOnOutsideClick: true,
                }}
                onShow={() => setIsDropdownVisible(true)}
                onHide={() => setIsDropdownVisible(false)}
                offset={[0, 4]}
                content={
                  <Menu>
                    {items
                      .slice(
                        visibleItemsLeftSide,
                        visibleItemsLeftSide + collapsedItems
                      )
                      .map((child) => (
                        <Menu.Item
                          key={child.label}
                          onClick={child.onClick}
                          icon={child.icon}
                          iconPlacement="left"
                          href={child.link}
                        >
                          {child.label}
                        </Menu.Item>
                      ))}
                  </Menu>
                }
              >
                <button
                  type="button"
                  className="cogs-v11 cogs-breadcrumbs__item__button cogs-breadcrumbs__dropdown-toggle"
                  aria-pressed={isDropdownVisible}
                  data-testid="cogs-breadcrumbs-collapsed-dropdown"
                >
                  <IconWrapper size={12}>
                    <EllipsisHorizontalIcon />
                  </IconWrapper>
                </button>
              </Dropdown>
            </div>
            <Separator />
          </>
        )}
        {renderBreadcrumbItems({
          start: visibleItemsLeftSide + collapsedItems,
          end: items.length,
        })}
      </Flex>
    </nav>
  );
};

const BreadcrumbsActiveItemContent = ({
  activeOptions,
  children,
  disabled,
  dropdownAppendTo,
  icon,
  label,
  onClick,
  _parentWrapperRef,
}: PropsWithChildren<
  Omit<BreadcrumbsItemProps, 'onClick'> & {
    onClick: (
      e: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>
    ) => void;
  }
>) => {
  const {
    value: isDropdownVisible,
    setFalse: onHide,
    setTrue: onShow,
  } = useBoolean();
  const {
    description,
    promoChip,
    tagChip,
    statusChip,
    actionMenu,
    actionMenuTarget = 'icon',
  } = activeOptions || {};

  const labelRef = useRef<HTMLElement>(null);
  const labelContainerRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLElement>(null);

  const labelMaxWidth = useResponsiveMaxWidth(
    _parentWrapperRef ?? { current: null },
    labelContainerRef,
    labelRef
  );
  const descriptionMaxWidth = useResponsiveMaxWidth(
    _parentWrapperRef ?? { current: null },
    descriptionRef,
    descriptionRef
  );

  const { getOverlayContainer } = useConfigContextLoose();

  const renderLabel = () => (
    <Tooltip content={label} disabled={!labelMaxWidth} placement="bottom">
      <span
        ref={labelRef}
        className="cogs-v11 cogs-breadcrumbs__item--active__label"
        style={{
          maxWidth: labelMaxWidth && `max(80px, ${labelMaxWidth})`,
        }}
      >
        {label}
      </span>
    </Tooltip>
  );

  const renderDescription = () => (
    <Tooltip
      content={description}
      disabled={!descriptionMaxWidth}
      placement="bottom"
    >
      <Body
        ref={descriptionRef}
        className="cogs-v11 cogs-breadcrumbs__item--active__description"
        size="xx-small"
        muted
        style={{
          maxWidth: descriptionMaxWidth && `max(80px, ${descriptionMaxWidth})`,
        }}
      >
        {description}
      </Body>
    </Tooltip>
  );

  if (actionMenuTarget === 'fullItem') {
    return (
      <Flex direction="column">
        <Flex
          ref={labelContainerRef}
          gap={8}
          alignItems="center"
          className={classNames(
            'cogs-v11',
            'cogs-breadcrumbs__item__container',
            'cogs-breadcrumbs__item__container--active'
          )}
        >
          {actionMenu ? (
            <Dropdown
              appendTo={
                dropdownAppendTo ?? getOverlayContainer?.() ?? undefined
              }
              hideOnSelect={{
                hideOnContentClick: true,
                hideOnOutsideClick: true,
              }}
              onShow={onShow}
              onHide={onHide}
              content={actionMenu}
              offset={[0, 4]}
            >
              <Button
                tabIndex={0}
                className={classNames(
                  'cogs-v11',
                  'cogs-breadcrumbs__item__button',
                  'cogs-breadcrumbs__item__button--active',
                  disabled && 'cogs-breadcrumbs__item__button--disabled'
                )}
                type="ghost"
                aria-haspopup={actionMenu ? 'menu' : 'false'}
                aria-pressed={isDropdownVisible}
                aria-current="page"
                onClick={onClick}
                disabled={disabled}
                toggled={isDropdownVisible}
              >
                {icon && (
                  <IconWrapper
                    size={14}
                    className="cogs-v11 cogs-breadcrumbs__icon-wrapper"
                  >
                    {icon}
                  </IconWrapper>
                )}
                {children ?? (label && renderLabel())}
                {isDropdownVisible ? (
                  <ChevronUpSmallIcon />
                ) : (
                  <ChevronDownSmallIcon />
                )}
              </Button>
            </Dropdown>
          ) : null}
          {promoChip}
          {tagChip}
          {statusChip}
        </Flex>
        {description && renderDescription()}
      </Flex>
    );
  }

  return (
    <Flex direction="column">
      <div
        ref={labelContainerRef}
        className={classNames(
          'cogs-v11',
          'cogs-breadcrumbs__item__container',
          'cogs-breadcrumbs__item__container--active'
        )}
      >
        <div
          className={classNames(
            'cogs-v11',
            'cogs-breadcrumbs__item__button',
            'cogs-breadcrumbs__item__button--active',
            disabled && 'cogs-breadcrumbs__item__button--disabled'
          )}
          onClick={onClick}
        >
          {icon && (
            <IconWrapper
              size={14}
              className="cogs-v11 cogs-breadcrumbs__icon-wrapper"
            >
              {icon}
            </IconWrapper>
          )}
          {children ?? (label && renderLabel())}
        </div>
        {actionMenu && (
          <Dropdown
            appendTo={dropdownAppendTo ?? getOverlayContainer?.() ?? undefined}
            hideOnSelect={{
              hideOnContentClick: true,
              hideOnOutsideClick: true,
            }}
            onShow={onShow}
            onHide={onHide}
            content={actionMenu}
            offset={[0, 4]}
            className="cogs-v11 cogs-breadcrumbs__item--active__dropdown"
          >
            <IconButton
              icon={
                isDropdownVisible ? ChevronUpSmallIcon : ChevronDownSmallIcon
              }
              toggled={isDropdownVisible}
            />
          </Dropdown>
        )}
        {promoChip}
        {tagChip}
        {statusChip}
      </div>
      {description && renderDescription()}
    </Flex>
  );
};

export const BreadcrumbsItem: React.ForwardRefExoticComponent<
  BreadcrumbsHtmlElementProps<HTMLDivElement> &
    BreadcrumbsItemProps &
    React.RefAttributes<HTMLDivElement>
> = forwardRef<
  HTMLDivElement,
  BreadcrumbsHtmlElementProps<HTMLDivElement> & BreadcrumbsItemProps
>((props, ref): React.ReactElement => {
  const { className, label, onClick, link, icon, active, disabled, children } =
    props;

  const handleClick = (
    e: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>
  ) => {
    if (onClick && !disabled) onClick(e);
  };

  const renderInactiveItem = () => (
    <>
      <a
        tabIndex={0}
        className={classNames(
          'cogs-v11',
          'cogs-breadcrumbs__item__button',
          disabled && 'cogs-breadcrumbs__item__button--disabled'
        )}
        aria-current="false"
        href={link}
        onClick={handleClick}
      >
        {icon && (
          <IconWrapper
            size={14}
            className="cogs-v11 cogs-breadcrumbs__icon-wrapper"
          >
            {icon}
          </IconWrapper>
        )}
        {children ?? (label && <span>{label}</span>)}
      </a>
      <Separator />
    </>
  );

  return (
    <Flex
      ref={ref}
      element="li"
      direction="row"
      alignItems="center"
      justifyContent="flex-start"
      className={classNames('cogs-v11', 'cogs-breadcrumbs__item', className)}
      data-chmln={props['data-chmln']}
    >
      {active ? (
        <BreadcrumbsActiveItemContent {...props} onClick={handleClick} />
      ) : (
        renderInactiveItem()
      )}
    </Flex>
  );
});

Breadcrumbs.Item = BreadcrumbsItem;
